// Support at Home Calculation Engine — cheap/reactive slice.
// Implements 1 November 2025 contribution rules.
// Ported verbatim from frontend/src/calculations/SupportAtHomeCalculations.js.
//
// Note: calculateSupportAtHomeContribution and calculateIndividualContributions (the actual
// means-test contribution engine) live in
// server/src/rest/calculation/supportAtHomeContributionEngine.ts — they're server-only
// (non-reactive, no shared caller), unlike the lifetime-cap helpers below which are cheap
// arithmetic used directly by the frontend.
import { DEEMING_RATE_LOWER, DEEMING_RATE_UPPER } from './constants/ratesAndThresholds.ts';

// Lifetime caps — combined with residential NCCC; indexed 20 Mar & 20 Sep. 20 September 2026 values
// (DoH residential schedule from 20 September 2026: NCCC lifetime cap; means-tested care fee lifetime cap).
export const LIFETIME_CAPS = {
    standard: 140652.80, // Standard rates cap (was 137917.01 @ 20 Mar 2026)
    nwop: 88934.57, // No Worse Off Principle cap, pre-13 Sep 2024 participants (was 86185.23 @ 20 Mar 2026)
};

// Deeming thresholds (from Age Pension)
// Schedule of contributions for Support at Home services, 1 July 2026 (thresholds index 1 July).
const DEEMING_THRESHOLDS = {
    single: 66800,
    couplePension: 110600,
    coupleNoPension: 55300,
};

const DEEMING_RATES = {
    lower: DEEMING_RATE_LOWER,
    upper: DEEMING_RATE_UPPER,
};

/** Calculate total lifetime contributions paid — tracks cumulative contributions over time. */
export const calculateLifetimeContributionPaid = (annualContribution: number, yearsOfCare: number): number => {
    return annualContribution * Math.min(yearsOfCare, 100); // Cap at 100 years as safety
};

/** Check if lifetime cap has been reached. */
export const isLifetimeCapReached = (cumulativeContributions: number, participantType: string = 'standard'): boolean => {
    const cap = participantType === 'nwop' ? LIFETIME_CAPS.nwop : LIFETIME_CAPS.standard;
    return cumulativeContributions >= cap;
};

/** Get years until lifetime cap reached. */
export const yearsUntilCapReached = (annualContribution: number, currentCumulative: number = 0, participantType: string = 'standard'): number | null => {
    if (annualContribution <= 0) return null;

    const cap = participantType === 'nwop' ? LIFETIME_CAPS.nwop : LIFETIME_CAPS.standard;
    const remainingCap = cap - currentCumulative;

    if (remainingCap <= 0) return 0;
    if (annualContribution >= cap) return Math.ceil(remainingCap / annualContribution);

    return Math.ceil(remainingCap / annualContribution);
};

// DEEMING_THRESHOLDS/DEEMING_RATES are defined but unused in this module (same as the original
// frontend file) — kept for parity rather than silently dropped.
void DEEMING_THRESHOLDS;
void DEEMING_RATES;
